import {SearchField, SvgIcon, Tooltip, Checkbox} from '@shipwell/shipwell-ui';
import {ChangeEvent, useRef, useState} from 'react';
import {WithRouterProps, withRouter} from 'react-router';
import {AddressBookEntry} from '@shipwell/backend-core-sdk';
import {Form, Formik, FormikHelpers, FormikProps} from 'formik';
import {object, string} from 'yup';
import {compose} from 'recompose';
import {connect} from 'react-redux';
import {Action, Dispatch} from 'redux';
import {SharedAddress} from '@shipwell/backend-core-singlerequestparam-sdk';
import {RowSelectionState} from '@tanstack/react-table';
import {AddOrdersToShipmentFooter, SharedStartEndpointFooter} from './components/ModalFooter';
import {mapAddressToBaseAddressSchema} from 'App/containers/purchaseOrders/list/components/ChangeCustomerModalContent/mappers';
import { AddOrdersToShipmentListTable} from 'App/containers/orders/views/AddOrdersToShipmentListView/components/AddOrdersToShipmentListTable';
import {FlexBox} from 'App/components/Box';
import {useAddOrdersToShipment} from 'App/data-hooks/shipmentAssembly/useAddOrdersToShipment';
import {parseV3ApiError} from 'App/api/typedUtils';
import {useLocalStorage} from 'App/utils/hooks/useLocalStorage';
import WithStatusToasts, {WithStatusToastProps} from 'App/components/withStatusToasts';
import {shipmentsShipmentIdGet} from 'App/actions/shipmentdetails';
import {
  AddOrdersToShipmentSharedStartEndPoint,
  SharedTypeEnum
} from 'App/containers/orders/views/AddOrdersToShipmentListView/components/AddOrdersToShipmentSharedStartEndPoint';
import {AddressSchema} from 'App/utils/yupHelpersTyped';

type AddOrdersToShipmentListViewProps = WithRouterProps<{shipment_id: string}> &
  WithStatusToastProps & {onClose: () => void; dispatch?: Dispatch<Action<typeof shipmentsShipmentIdGet>>};
export type SubmitValuesType = {
  sharedType: SharedTypeEnum;
  shared_address?: AddressBookEntry['address'];
  formikActions?: FormikHelpers<SubmitValuesType>;
  start_time?: string | null;
  end_time?: string | null;
};
const validationSchema = object().shape({
  shared_address: AddressSchema.test('shared_address', 'A valid address is required.', (value) => !!value?.country),
  start_time: string()
    .nullable()
    .when('shouldShareAddress', {
      is: true,
      then: string().nullable().required('A valid date is required.')
    }),
  end_time: string().nullable()
});
const AddOrdersToShipmentListViewBase = ({
  params,
  onClose,
  setSuccess,
  setError,
  dispatch
}: AddOrdersToShipmentListViewProps) => {

  const {addOrdersToShipment, isAddingOrdersToShipment} = useAddOrdersToShipment();
  const [selectedOrderIds, setSelectedOrderIds] = useState<string[]>([]);
  const [rowSelection, setRowSelection] = useState<RowSelectionState>({});
  const [searchTerm, setSearchTerm] = useState('');
  const [displayAllAvailableOrders, setDisplayAllAvailableOrders] = useLocalStorage({key: 'display-all-available-orders', initialValue: false});
  const [isModifyingOrders, setIsModifyingOrders] = useState(false);
  const handleSearchInput = (e: ChangeEvent<HTMLInputElement>) => setSearchTerm(e.target.value);
  const formRef = useRef<FormikProps<SubmitValuesType>>(null);

  const initialValues = {
    sharedType: SharedTypeEnum.Start,
    shared_address: {country: ''},
    start_time: null,
    end_time: null
  };
  const handleSubmit = (values: SubmitValuesType) => {
    handleAddOrdersToShipment({
      ...(values?.shared_address
        ? {
            [values.sharedType === SharedTypeEnum.Start ? 'sharedOrigin' : 'sharedDestination']: {
              ...mapAddressToBaseAddressSchema(values.shared_address),
              start_time: values.start_time,
              end_time: values.end_time,
              //Instead of adding a reference, we are supposed
              //to add the address book entry id in this param if it exists.
              address_book_entry: values.shared_address.id
            }
          }
        : {})
    });
  };
  const handleAddOrdersToShipment = (sharedAddress?: {
    sharedOrigin?: SharedAddress;
    sharedDestination?: SharedAddress;
  }) => {
    addOrdersToShipment(
      {
        shipmentId: params.shipment_id,
        orderIds: selectedOrderIds,
        sharedDestination: sharedAddress?.sharedDestination,
        sharedOrigin: sharedAddress?.sharedOrigin
      },
      {
        onSuccess: () => {
          setSuccess('Success!', 'Orders were successfully added to shipment.');
          //We don't have any of the modern redux tools to type actions correctly, but we don't want to make
          //all of the API calls in fetchDetails.
          if (dispatch) {
            void dispatch(shipmentsShipmentIdGet(params.shipment_id, {}) as unknown as Action);
          }
          onClose();
        },
        onError: (error) => setError('Error!', parseV3ApiError(error).detail)
      }
    );
  };
  const DysplayAllAvailableOrdersTooltipContent = () => (
    <div>
      <span className='font-bold'>All Available Orders</span><br/>
      <span>This displays all orders no matter their date.<br/> This will not include orders that are in the ‘Not Ready’, ’Filled’, and ‘Delivered’ status.</span>
    </div>
  )
  return (
    <FlexBox gap="m" direction="col">
      {!isModifyingOrders ? (
        <>
          <span>Select the order(s) you want to add to this shipment.</span>
          <div className="border border-sw-border">
            <FlexBox gap="s" direction="row" items="center" justify="between" pad="m">
              <div className="flex  gap-1">
                <h5>Orders</h5>
              </div>
              <div className="flex items-center gap-1">
                <div className='flex gap-1'>
                  <Checkbox
                    label="Display all available orders"
                    checked={displayAllAvailableOrders}
                    onChange={() => setDisplayAllAvailableOrders(!displayAllAvailableOrders)}
                    fixedHeight={false}
                  />
                  <Tooltip tooltipContent={<DysplayAllAvailableOrdersTooltipContent />} tooltipClassname={'max-w-[400px]'}>
                    <SvgIcon name="InfoOutlined" color="$sw-icon"/>
                  </Tooltip>
                </div>
                <div className="grow">
                  <SearchField
                    label="Search for Orders"
                    name="search orders"
                    value={searchTerm}
                    onChange={handleSearchInput}
                  />
                </div>
              </div>
            </FlexBox>
            <AddOrdersToShipmentListTable
              searchTerm={searchTerm}
              displayAllAvailableOrders={displayAllAvailableOrders}
              onRowSelectionChange={setSelectedOrderIds}
              rowSelection={rowSelection}
              setRowSelection={setRowSelection}
            />
          </div>
        </>
      ) : (
        <Formik
          validationSchema={validationSchema}
          initialValues={initialValues}
          onSubmit={handleSubmit}
          innerRef={formRef}
        >
          <Form noValidate>
            <AddOrdersToShipmentSharedStartEndPoint numberOfOrders={selectedOrderIds.length} />
          </Form>
        </Formik>
      )}
      {!isModifyingOrders ? (
        <AddOrdersToShipmentFooter
          onClose={onClose}
          isModifyingOrders={isModifyingOrders}
          isAddingOrdersToShipment={isAddingOrdersToShipment}
          selectedOrderIds={selectedOrderIds}
          setIsModifyingOrders={setIsModifyingOrders}
          handleAddOrdersToShipment={handleAddOrdersToShipment}
        />
      ) : (
        <SharedStartEndpointFooter
          onClose={onClose}
          isAddingOrdersToShipment={isAddingOrdersToShipment}
          selectedOrderIds={selectedOrderIds}
          handleSubmit={() => formRef.current?.handleSubmit()}
          setIsModifyingOrders={setIsModifyingOrders}
        />
      )}
    </FlexBox>
  );
};

export const AddOrdersToShipmentListView = compose<
  AddOrdersToShipmentListViewProps,
  //the outer props, or what the component receives aside from the HOC props
  {onClose: () => void}
>(
  withRouter,
  WithStatusToasts,
  connect()
)(AddOrdersToShipmentListViewBase);
